import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  CheckCircle, Clock, Bell, CalendarDays, Trash2, Pin, Wallet, FileText, Landmark,
  Plus, Leaf, Gift, Sparkles, X, ChevronRight, Circle, MessageCircle,
  Shield, Lock, UserCircle2, Mail, Globe, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ShareWhatsappButton from './ShareWhatsappButton';
import { safeDate } from '../lib/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const WEEKDAYS_SHORT = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Preset visual por color de aviso (mockup 2026-02-28).
const ANN_PRESETS = {
  yellow: {
    Icon: Landmark, stripe: '#F97316',
    iconBg: 'bg-orange-100 dark:bg-orange-500/15',
    iconColor: 'text-orange-600 dark:text-orange-400',
    darkGlow: 'dark:shadow-[inset_2px_0_0_0_rgba(249,115,22,0.55)]',
  },
  red: {
    Icon: Plus, stripe: '#EF4444',
    iconBg: 'bg-red-100 dark:bg-red-500/15',
    iconColor: 'text-red-600 dark:text-red-400',
    darkGlow: 'dark:shadow-[inset_2px_0_0_0_rgba(239,68,68,0.55)]',
  },
  green: {
    Icon: Leaf, stripe: '#10B981',
    iconBg: 'bg-emerald-100 dark:bg-emerald-500/15',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    darkGlow: 'dark:shadow-[inset_2px_0_0_0_rgba(16,185,129,0.55)]',
  },
};

const parseWhen = (str) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Bloque compartido para el título de cada card premium.
const CardHeader = ({ icon: Icon, iconColor, title, action, actionLabel = 'Ver todos →', extra, compact = false }) => (
  <div className="flex items-center justify-between mb-4 gap-2">
    <div className="flex items-center gap-1.5 min-w-0">
      <Icon className={`w-4 h-4 flex-shrink-0 ${iconColor}`} strokeWidth={2} />
      <h2 className={`font-extrabold uppercase text-[#2563EB] dark:text-[#60A5FA] truncate ${compact ? 'text-[11px] tracking-[0.08em]' : 'text-[12px] sm:text-[13px] tracking-[0.14em]'}`}>
        {title}{extra}
      </h2>
    </div>
    {action && (
      <button
        onClick={action}
        className="text-[11px] font-semibold text-[#2563EB] dark:text-[#60A5FA] hover:underline flex-shrink-0"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// "Para vos hoy" — reemplaza el DailyUpliftCard usando el mismo endpoint.
function ParaVosHoyCard() {
  const [phrase, setPhrase] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/daily/uplift`, { withCredentials: true });
        if (alive) setPhrase(data?.phrase || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);
  if (!phrase || dismissed) return null;
  return (
    <div data-testid="para-vos-hoy-card" className="premium-card p-4 sm:p-5 relative overflow-hidden">
      <div className="flex items-center gap-3 pr-16">
        <div className="w-11 h-11 rounded-2xl bg-[#EEF2FF] dark:bg-[#3B82F6]/15 flex items-center justify-center flex-shrink-0">
          <Gift className="w-5 h-5 text-[#2563EB] dark:text-[#60A5FA]" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#2563EB] dark:text-[#60A5FA]">
            Para vos hoy
          </p>
          <p data-testid="para-vos-hoy-phrase" className="mt-0.5 text-sm sm:text-[15px] text-slate-700 dark:text-slate-300 leading-snug">
            {phrase}
          </p>
        </div>
      </div>
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#2563EB]/70 dark:text-[#60A5FA]/80" strokeWidth={2} />
        <button
          aria-label="Cerrar"
          onClick={() => setDismissed(true)}
          className="w-6 h-6 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

// Footer premium — 5 tiles + soporte.
function DashboardFooter() {
  const tiles = [
    { icon: Lock, title: 'Seguro y confiable', desc: 'Tus datos están protegidos con los más altos estándares de seguridad.' },
    { icon: Shield, title: 'Privacidad ante todo', desc: 'No compartimos tu información con terceros.', link: 'Ver Política de Privacidad' },
    { icon: UserCircle2, title: '100% Personalizable', desc: 'Configurá V&O según tu forma de organizar tu vida.' },
    { icon: Mail, title: 'Siempre con vos', desc: 'Recordatorios, avisos y tareas siempre al alcance.' },
  ];
  return (
    <div className="mt-8 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {tiles.map(({ icon: Icon, title, desc, link }) => (
          <div key={title} className="premium-card p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#EEF2FF] dark:bg-[#3B82F6]/15 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{title}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-snug">{desc}</p>
              {link && (
                <a href="#privacy" className="text-[11px] font-semibold text-[#2563EB] dark:text-[#60A5FA] mt-1 inline-block hover:underline">{link}</a>
              )}
            </div>
          </div>
        ))}
        <div className="premium-card p-4 flex items-center gap-3">
          <h3 className="text-3xl font-display font-extrabold leading-none">
            <span className="text-slate-900 dark:text-white">V</span>
            <span className="text-[#2563EB] dark:text-[#3B82F6]">&amp;</span>
            <span className="text-slate-900 dark:text-white">O</span>
          </h3>
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Vida en Orden</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-snug">Menos caos. Más enfoque.<br/>Más vida.</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-xs text-slate-500 dark:text-slate-400 pt-4 pb-2">
        <div className="flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" strokeWidth={2} />
          <a href="mailto:hola@vidaenorden.ar" className="hover:text-[#2563EB] dark:hover:text-[#60A5FA]">hola@vidaenorden.ar</a>
        </div>
        <span className="hidden sm:inline text-slate-300 dark:text-slate-600">|</span>
        <div className="flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5" strokeWidth={2} />
          <a href="https://www.vidaenorden.ar" target="_blank" rel="noopener noreferrer" className="hover:text-[#2563EB] dark:hover:text-[#60A5FA]">www.vidaenorden.ar</a>
        </div>
      </div>
    </div>
  );
}

export default function TodayView() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({ tasks: [], reminders: [], notifications: [] });
  const [allTasks, setAllTasks] = useState([]);
  const [allReminders, setAllReminders] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [expensesSummary, setExpensesSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    try {
      const period = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      })();
      const [dashRes, tasksRes, remindersRes, annRes, expRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/today`, { withCredentials: true }),
        axios.get(`${API}/api/tasks`, { withCredentials: true }),
        axios.get(`${API}/api/reminders`, { withCredentials: true }),
        axios.get(`${API}/api/announcements`, { withCredentials: true }),
        axios.get(`${API}/api/expenses/summary?period=${period}`, { withCredentials: true }),
      ]);
      setDashboardData(dashRes.data);
      setAllTasks(tasksRes.data || []);
      setAllReminders(remindersRes.data || []);
      setAnnouncements(annRes.data || []);
      setExpensesSummary(expRes.data || null);
    } catch (error) {
      toast.error('Error al cargar el dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    const handler = () => loadDashboard();
    window.addEventListener('vida:data-changed', handler);
    return () => window.removeEventListener('vida:data-changed', handler);
  }, [loadDashboard]);

  // Filtro de notificaciones activas: solo no-leídas Y de los últimos 7 días.
  // Las notificaciones muy viejas ensucian el widget y confunden al usuario.
  const activeNotifications = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return (dashboardData.notifications || [])
      .filter((n) => {
        if (n.read) return false;
        if (n.deleted) return false;
        const ts = n.created_at ? new Date(n.created_at).getTime() : 0;
        return ts >= sevenDaysAgo;
      })
      .slice(0, 4);
  }, [dashboardData.notifications]);

  const markTaskComplete = async (taskId) => {
    try {
      await axios.patch(`${API}/api/tasks/${taskId}`, { completed: true }, { withCredentials: true });
      toast.success('Tarea completada');
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch { toast.error('Error al actualizar la tarea'); }
  };
  const deleteTask = async (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await axios.delete(`${API}/api/tasks/${taskId}`, { withCredentials: true });
      toast.success('Tarea eliminada');
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch { toast.error('No pudimos eliminar la tarea'); }
  };

  // Próximos 7 días — dots por día (tareas/eventos).
  const next7Days = useMemo(() => {
    const today = startOfDay(new Date());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      days.push({ date: d, tasks: [], reminders: [] });
    }
    const endWindow = new Date(today); endWindow.setDate(today.getDate() + 7);
    allTasks.forEach((t) => {
      if (t.completed) return;
      const when = parseWhen(t.due_date); if (!when) return;
      const day = startOfDay(when);
      if (day >= today && day < endWindow) {
        const idx = Math.round((day - today) / 86400000);
        days[idx].tasks.push(t);
      }
    });
    allReminders.forEach((r) => {
      const when = parseWhen(r.remind_at); if (!when) return;
      const day = startOfDay(when);
      if (day >= today && day < endWindow) {
        const idx = Math.round((day - today) / 86400000);
        days[idx].reminders.push(r);
      }
    });
    return days;
  }, [allTasks, allReminders]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Gastos del mes (multi-moneda: mismos aliases que la versión previa)
  const exp = expensesSummary || {};
  const curr = exp.primary_currency || exp.currency || 'ARS';
  const total = exp.total_in_primary != null ? exp.total_in_primary : exp.total || 0;
  const paid = exp.paid_in_primary != null ? exp.paid_in_primary : exp.paid || 0;
  const pending = exp.pending_in_primary != null ? exp.pending_in_primary : exp.pending || 0;
  const unconverted = exp.unconverted_count || 0;
  const fmt = (n) => `${curr} ${Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  const paidPctLabel = total > 0 ? `${(paid / total * 100).toFixed(1)}%` : '0%';
  const bancoPctLabel = total > 0 ? `${(pending / total * 100).toFixed(1)}%` : '0%';

  return (
    <div className="space-y-5" data-testid="today-view">
      {/* Header */}
      <div>
        <h1 className="text-4xl sm:text-5xl font-display font-extrabold tracking-tight text-slate-900 dark:text-white">
          Hoy
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-base mt-1">Todo lo que necesitás para este día</p>
      </div>

      {/* Para vos hoy */}
      <ParaVosHoyCard />

      {/* Row: Próximos 7 días + Gastos del mes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* PRÓXIMOS 7 DÍAS */}
        <div data-testid="next-7-days-widget" className="premium-card p-5 sm:p-6">
          <CardHeader
            icon={CalendarDays}
            iconColor="text-[#2563EB] dark:text-[#60A5FA]"
            title="Próximos 7 días"
            action={() => navigate('/dashboard/calendar')}
            actionLabel="Ver calendario →"
          />
          <div className="grid grid-cols-7 gap-1.5">
            {next7Days.map(({ date, tasks, reminders }, i) => {
              const isToday = i === 0;
              const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const hasTask = tasks.length > 0;
              const hasEvent = reminders.length > 0;
              return (
                <button
                  key={key}
                  data-testid={`widget-day-${key}`}
                  onClick={() => navigate('/dashboard/calendar')}
                  className={`flex flex-col items-center py-3 px-0.5 rounded-2xl transition-all ${
                    isToday
                      ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/25'
                      : 'bg-white dark:bg-white/[0.03] hover:bg-slate-50 dark:hover:bg-white/[0.06] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <span className={`text-[9px] font-bold uppercase tracking-wider ${isToday ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                    {WEEKDAYS_SHORT[date.getDay()]}
                  </span>
                  <span className={`text-lg font-display font-extrabold leading-tight mt-1 ${isToday ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                    {date.getDate()}
                  </span>
                  <span className={`text-[9px] mt-0.5 ${isToday ? 'text-white/70' : 'text-slate-400 dark:text-slate-500'}`}>
                    {MONTHS_SHORT[date.getMonth()]}
                  </span>
                  <div className="mt-2 h-3 flex items-center gap-1.5">
                    {hasEvent && (
                      <span title={`${reminders.length} evento(s)`} className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
                    )}
                    {hasTask && (
                      <span title={`${tasks.length} tarea(s)`} className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                    )}
                    {!hasEvent && !hasTask && (
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </div>
                  <div className={`mt-0.5 text-[9px] ${isToday ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>
                    {hasEvent && `${reminders.length} evento${reminders.length > 1 ? 's' : ''}`}
                    {!hasEvent && hasTask && `${tasks.length} tarea${tasks.length > 1 ? 's' : ''}`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* GASTOS DEL MES */}
        <div data-testid="expenses-widget" className="premium-card p-5 sm:p-6 relative">
          <CardHeader
            icon={Wallet}
            iconColor="text-[#2563EB] dark:text-[#60A5FA]"
            title="Gastos del mes"
            action={() => navigate('/dashboard/expenses')}
            actionLabel="Ver gastos →"
          />
          <p className="text-3xl sm:text-4xl font-display font-extrabold text-slate-900 dark:text-white leading-none tracking-tight" data-testid="widget-exp-total">
            {fmt(total)}
          </p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded-2xl p-4 bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-200 dark:ring-emerald-500/20">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Pagado</p>
              <p className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mt-1" data-testid="widget-exp-paid">{fmt(paid)}</p>
              <p className="text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold mt-0.5">{paidPctLabel} del total</p>
            </div>
            <div className="rounded-2xl p-4 bg-slate-50 dark:bg-white/[0.03] ring-1 ring-slate-200 dark:ring-white/10">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-600 dark:text-slate-400">Banco</p>
              <p className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mt-1" data-testid="widget-exp-pending">{fmt(pending)}</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold mt-0.5">{bancoPctLabel} del total</p>
            </div>
          </div>
          <div className="mt-4 w-full h-2 bg-slate-100 dark:bg-white/[0.05] rounded-full overflow-hidden">
            <div data-testid="widget-exp-progress" className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">{pct}% pagado de {exp.count || 0} gastos</p>
          {unconverted > 0 && (
            <p data-testid="widget-exp-unconverted" className="text-[11px] text-orange-600 dark:text-orange-400 font-semibold mt-1">
              {unconverted} sin tipo de cambio — no se sumaron al total en {curr}
            </p>
          )}
          <button
            onClick={() => navigate('/dashboard/expenses')}
            data-testid="widget-exp-add"
            aria-label="Agregar gasto"
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#2563EB] hover:brightness-110 text-white flex items-center justify-center shadow-lg shadow-[#2563EB]/30 transition-all"
          >
            <Plus className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Row: 4 columnas — Muro / Tareas / Recordatorios / Notificaciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {/* MURO DE AVISOS */}
        <div className="premium-card p-4 sm:p-5" data-testid="announcements-widget">
          <CardHeader
            icon={FileText}
            iconColor="text-orange-500 dark:text-orange-400"
            title="Muro de avisos"
            action={() => navigate('/dashboard/announcements')}
            compact
          />
          <div className="space-y-2.5">
            {announcements.length === 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center py-6">Sin avisos por ahora</p>
            )}
            {announcements.slice(0, 3).map((a) => {
              const preset = ANN_PRESETS[a.color] || ANN_PRESETS.yellow;
              const CatIcon = preset.Icon;
              return (
                <button
                  key={a.id}
                  data-testid={`widget-announcement-${a.id}`}
                  onClick={() => navigate('/dashboard/announcements')}
                  className={`group relative w-full text-left rounded-2xl overflow-hidden bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all ${preset.darkGlow}`}
                >
                  <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5 dark:opacity-60" style={{ backgroundColor: preset.stripe }} />
                  <div className="flex items-start gap-2.5 px-3 py-3 pl-4">
                    <div className={`w-9 h-9 rounded-xl ${preset.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <CatIcon className={`w-4 h-4 ${preset.iconColor}`} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0 pr-5">
                      <p className="font-bold text-slate-900 dark:text-white truncate text-sm leading-tight">{a.title}</p>
                      {a.message && (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2 leading-snug">{a.message}</p>
                      )}
                    </div>
                    <Pin className="absolute top-3 right-2.5 w-3.5 h-3.5 text-orange-500 dark:text-orange-400 rotate-45" strokeWidth={2} fill="currentColor" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* TAREAS PENDIENTES */}
        <div className="premium-card p-4 sm:p-5">
          <CardHeader
            icon={CheckCircle}
            iconColor="text-emerald-500 dark:text-emerald-400"
            title="Tareas pendientes"
            extra={dashboardData.tasks.length > 0 && (
              <span className="ml-1 text-slate-400 dark:text-slate-500"> · {dashboardData.tasks.length}</span>
            )}
            action={() => navigate('/dashboard/tasks')}
            actionLabel="Ver todas →"
            compact
          />
          {dashboardData.tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <CheckCircle className="w-12 h-12 text-slate-300 dark:text-slate-600" strokeWidth={1.4} />
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center">¡No hay tareas pendientes!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {dashboardData.tasks.slice(0, 4).map((task) => (
                <div key={task.id} data-testid={`task-item-${task.id}`} className="flex items-center gap-2 group rounded-xl px-2 -mx-2 py-1 hover:bg-slate-50 dark:hover:bg-white/[0.03] transition-colors">
                  <button
                    data-testid={`task-complete-${task.id}`}
                    onClick={(e) => { e.stopPropagation(); markTaskComplete(task.id); }}
                    className="w-5 h-5 rounded-full border-2 border-slate-300 dark:border-slate-600 hover:border-emerald-500 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
                    aria-label="Marcar completa"
                  />
                  <button
                    onClick={() => navigate('/dashboard/tasks')}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`task-open-${task.id}`}
                  >
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate leading-tight">{task.title}</p>
                    {task.due_date && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1">
                        Vence: {safeDate(task.due_date, undefined, task.due_date)}
                        {task.due_time && <><Clock className="w-2.5 h-2.5" strokeWidth={2.5} />{task.due_time}</>}
                      </p>
                    )}
                  </button>
                  {task.due_date && (
                    <ShareWhatsappButton title={task.title} date={task.due_date} description={task.description} kind="task" testId={`share-task-${task.id}`} />
                  )}
                  <button
                    data-testid={`delete-task-${task.id}`}
                    onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }}
                    aria-label="Eliminar tarea"
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RECORDATORIOS */}
        <div className="premium-card p-4 sm:p-5">
          <CardHeader
            icon={Bell}
            iconColor="text-[#2563EB] dark:text-[#60A5FA]"
            title="Recordatorios"
            action={() => navigate('/dashboard/calendar?tab=reminders')}
            compact
          />
          {dashboardData.reminders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 gap-3 text-center">
              <div className="w-16 h-16 rounded-full bg-[#EEF2FF] dark:bg-[#3B82F6]/10 flex items-center justify-center">
                <Bell className="w-8 h-8 text-[#2563EB] dark:text-[#60A5FA]" strokeWidth={1.6} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">No tenés recordatorios pendientes</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 flex items-center justify-center gap-1">
                  Podés pedírselos a NEO cuando quieras.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {dashboardData.reminders.slice(0, 4).map((rem) => (
                <button
                  key={rem.id}
                  data-testid={`reminder-item-${rem.id}`}
                  onClick={() => navigate('/dashboard/calendar?tab=reminders')}
                  className="w-full flex items-start gap-2 rounded-xl px-2 -mx-2 py-1.5 hover:bg-slate-50 dark:hover:bg-white/[0.03] transition-colors text-left"
                >
                  <Circle className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA] mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate leading-tight">{rem.title}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      {safeDate(rem.remind_at, undefined, '')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* NOTIFICACIONES RECIENTES */}
        <div className="premium-card p-4 sm:p-5 relative">
          <CardHeader
            icon={Bell}
            iconColor="text-[#2563EB] dark:text-[#60A5FA]"
            title="Notificaciones recientes"
            action={() => navigate('/dashboard')}
            compact
          />
          {activeNotifications.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center py-6">Sin notificaciones recientes</p>
          ) : (
            <div className="space-y-1">
              {activeNotifications.map((n) => {
                const t = (n.type || '').toLowerCase();
                const NIcon = t.includes('gasto') || t.includes('expense') ? DollarSign
                  : t.includes('event') || t.includes('cal') ? CalendarDays
                  : Bell;
                const iconColor = t.includes('gasto') || t.includes('expense') ? 'text-emerald-500'
                  : t.includes('event') || t.includes('cal') ? 'text-orange-500'
                  : 'text-[#2563EB]';
                return (
                  <button
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => n.link ? navigate(n.link) : navigate('/dashboard/family')}
                    className="w-full flex items-start gap-2.5 rounded-xl px-2 -mx-2 py-1.5 hover:bg-slate-50 dark:hover:bg-white/[0.03] transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                      <NIcon className={`w-4 h-4 ${iconColor}`} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate leading-tight">{n.title}</p>
                      {n.message && (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2 leading-snug">{n.message}</p>
                      )}
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 mt-1 flex-shrink-0" strokeWidth={2.5} />
                  </button>
                );
              })}
            </div>
          )}
          <button
            onClick={() => navigate('/dashboard/tasks')}
            aria-label="Crear"
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#2563EB] hover:brightness-110 text-white flex items-center justify-center shadow-lg shadow-[#2563EB]/30 transition-all"
          >
            <Plus className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Footer */}
      <DashboardFooter />
    </div>
  );
}
